import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AnalyticsProduct,
  FeedbackProduct,
  ProductCatalog,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import type { FeatureGroup, Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import { useOwnedAccount, mapOwnedAccountModelToDto } from '@hotjar/account';
import { getBundledPlanId } from '@hotjar/webapp/domains/common/product/utils';
import { sortBy, find } from 'lodash-es';
import { planById } from '@hotjar/webapp/domains/revenue/plan/utils';
import type { AccountTrialInfo, RegistrationCampaign } from '@hotjar/account';
import { getTrialInfo } from '@hotjar/webapp/domains/activation/trials/get-trial-info';
import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';
import type { PricingTable } from '@hotjar/webapp/domains/revenue/pricing/types';

type ActiveMultipleSkuPlans = {
  [ProductType.Analytics]: AnalyticsProduct | null;
  [ProductType.Feedback]: FeedbackProduct | null;
};

function isBasicGroup(plan: AnalyticsProduct | FeedbackProduct) {
  return !plan || plan.tier === 'basic';
}

export const useNewSitePlan = (): {
  activeMultipleSkuPlans: ActiveMultipleSkuPlans;
  productCatalog: ProductCatalog | undefined;
  setActiveMultipleSkuPlan: (
    productType: ProductType,
    product: AnalyticsProduct | FeedbackProduct,
  ) => void;
  isPaidPlan: boolean;
  selectedPlan: Pick<Plan, 'identifier' | 'featureGroup'> | null;
  sitePlanInfo: { plan: FeatureGroup; planId: string } | null;
  trialInfo: AccountTrialInfo | null;
  candidateCampaign: Omit<RegistrationCampaign, 'numFreeDays'> | null;
  accountPricingTable: PricingTable;
} => {
  const { data: ownedAccount } = useOwnedAccount();
  const { data: productCatalog } = useProductCatalog();

  const [trialInfo, setTrialInfo] = useState<AccountTrialInfo | null>(null);

  const [candidateCampaign, setCandidateCampaign] = useState<Omit<
    RegistrationCampaign,
    'numFreeDays'
  > | null>(null);

  const [activeMultipleSkuPlans, setActiveMultipleSkuPlans] = useState<ActiveMultipleSkuPlans>({
    [ProductType.Analytics]: null,
    [ProductType.Feedback]: null,
  });

  useEffect(() => {
    setActiveMultipleSkuPlans({
      [ProductType.Analytics]: productCatalog?.analyticsProducts?.[0] ?? null,
      [ProductType.Feedback]: productCatalog?.feedbackProducts?.[0] ?? null,
    });
  }, [productCatalog]);

  const setActiveMultipleSkuPlan = useCallback(
    (productType: ProductType, product: AnalyticsProduct | FeedbackProduct) => {
      setActiveMultipleSkuPlans((prevState) => ({
        ...prevState,
        [productType]: product,
      }));
    },
    [],
  );

  const ownedAccountDto = useMemo(
    () => (ownedAccount ? mapOwnedAccountModelToDto(ownedAccount) : undefined),
    [ownedAccount],
  );

  const isPaidPlan = useMemo(() => {
    return Object.values(activeMultipleSkuPlans).some(
      (productType) => productType && !isBasicGroup(productType),
    );
  }, [activeMultipleSkuPlans]);

  const { pricingTable: accountPricingTable } = usePricingTableByAccount(ownedAccountDto);

  const plans = useMemo(() => {
    return Object.keys(accountPricingTable).map(planById);
  }, [accountPricingTable]);

  const basicPlan = useMemo(() => {
    return find(sortBy(plans, ['featureGroup', 'sampleRate']), { featureGroup: 'basic' });
  }, [plans]);

  const [selectedPlan, setSelectedPlan] = useState<Pick<
    Plan,
    'identifier' | 'featureGroup'
  > | null>(basicPlan ?? null);

  useEffect(() => {
    const analytics = activeMultipleSkuPlans[ProductType.Analytics];
    const feedback = activeMultipleSkuPlans[ProductType.Feedback];

    if (analytics && feedback) {
      setSelectedPlan({
        identifier: getBundledPlanId({
          analyticsPlanId: analytics.sku,
          feedbackPlanId: feedback.sku,
        }),
        featureGroup: analytics.tier || feedback.tier,
      });
    }
  }, [activeMultipleSkuPlans]);

  useEffect(() => {
    if (ownedAccount) {
      const trial = getTrialInfo(ownedAccount, ownedAccount);
      const activeTrialInfo = trial.activeOrEligible ? trial : null;
      setTrialInfo(activeTrialInfo);

      if (activeTrialInfo?.campaign) {
        setCandidateCampaign(activeTrialInfo.campaign);
      }
    } else if (basicPlan) {
      setCandidateCampaign({
        plan: basicPlan.featureGroup,
        planId: basicPlan.identifier,
        sampleRate: basicPlan.sampleRate,
      });
    }
  }, [basicPlan, ownedAccount]);

  const sitePlanInfo = useMemo(() => {
    const campaignPlan = plans.find((item) => item?.identifier === candidateCampaign?.planId);
    if (campaignPlan?.featureGroup && campaignPlan?.identifier && trialInfo?.activeOrEligible) {
      return {
        plan: campaignPlan.featureGroup,
        planId: campaignPlan.identifier,
      };
    }

    if (selectedPlan) {
      return {
        plan: selectedPlan.featureGroup,
        planId: selectedPlan.identifier,
      };
    }

    return null;
  }, [plans, trialInfo?.activeOrEligible, selectedPlan, candidateCampaign?.planId]);

  return {
    activeMultipleSkuPlans,
    productCatalog,
    setActiveMultipleSkuPlan,
    isPaidPlan,
    sitePlanInfo,
    trialInfo,
    candidateCampaign,
    accountPricingTable,
    selectedPlan,
  };
};
